import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';
import { UploadFileOutlined as UploadIcon } from '@mui/icons-material';
import type { Checkpoint } from '../../api/services/hiring.service';
import { checkpointLabel } from './hiringUI';

// Filing one document against a checkpoint.
//
// This exists because of a bug that had been there since the beginning and
// looked like something else entirely: a driver's licence and a medical
// certificate both state their own expiry, so the vault refuses to file
// one without a date (EXPIRY_REQUIRED). The hiring screen opened a bare
// file picker and posted the file with no date at all — so uploading a
// licence or an MEC from this page had NEVER worked, for any file type.
// It surfaced as "HEIC does not upload", because a licence is the thing
// somebody photographs on a phone.
//
// Two of the four document checkpoints need a date; the other two do not.
// Asking for it only where it is required keeps the common case to one
// click.

/** Which document types state their own expiry and cannot be filed without one. */
const NEEDS_EXPIRY = ['driver_license', 'mec'];

export function needsExpiry(checkpoint: Checkpoint | null): boolean {
  return !!checkpoint?.documentTypeKey && NEEDS_EXPIRY.includes(checkpoint.documentTypeKey);
}

const UploadCheckpointDialog: React.FC<{
  open: boolean;
  checkpoint: Checkpoint | null;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onUpload: (file: File, expiresAt: string | null) => void;
}> = ({ open, checkpoint, busy, error, onClose, onUpload }) => {
  const [file, setFile] = useState<File | null>(null);
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (!open) { setFile(null); setExpiresAt(''); }
  }, [open]);

  const wantsExpiry = needsExpiry(checkpoint);
  const ready = !!file && (!wantsExpiry || !!expiresAt);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{checkpoint ? checkpointLabel(checkpoint) : 'Add a file'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Button
            component="label"
            variant={file ? 'outlined' : 'contained'}
            disableElevation
            startIcon={<UploadIcon />}
            fullWidth
          >
            {file ? file.name : 'Choose a file'}
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/heic,image/heif,application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>

          {wantsExpiry && (
            <TextField
              label="Expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          )}

          {wantsExpiry && !expiresAt && (
            <Typography variant="caption" color="text.disabled">
              The date is on the document itself.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!ready || busy}
          onClick={() => file && onUpload(file, wantsExpiry ? expiresAt : null)}
        >
          {busy ? 'Filing…' : 'File it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadCheckpointDialog;
